import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import Layout from "@/components/Layout";
import { ScrollToTop } from "@/components/ScrollToTop";
import { WhatsAppConversionTracker } from "@/components/WhatsAppConversionTracker";
import { CallConversionTracker } from "@/components/CallConversionTracker";
import Home from "@/pages/Home";
import WeddingCateringMumbai from "@/pages/WeddingCateringMumbai";
import CorporateCateringMumbai from "@/pages/CorporateCateringMumbai";
import BirthdayFamilyCateringMumbai from "@/pages/BirthdayFamilyCateringMumbai";
import VegetarianCateringMumbai from "@/pages/VegetarianCateringMumbai";
import CateringForLargeEventsMumbai from "@/pages/CateringForLargeEventsMumbai";
import Menu from "@/pages/Menu";
import Packages from "@/pages/Packages";
import BuildYourMenu from "@/pages/BuildYourMenu";
import Cart from "@/pages/Cart";
import RequestQuote from "@/pages/RequestQuote";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import CustomerLogin from "@/pages/account/CustomerLogin";
import CustomerForgotPassword from "@/pages/account/CustomerForgotPassword";
import CustomerResetPassword from "@/pages/account/CustomerResetPassword";
import CustomerRegister from "@/pages/account/CustomerRegister";
import CustomerDashboard from "@/pages/account/CustomerDashboard";
import ProfileEdit from "@/pages/account/ProfileEdit";
import QuoteReview from "@/pages/account/QuoteReview";
import BookingDetail from "@/pages/account/BookingDetail";
import PrintableQuote from "@/pages/PrintableQuote";
import ProtectedCustomer from "@/components/ProtectedCustomer";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminRecovery from "@/pages/admin/AdminRecovery";
import AdminForgotPassword from "@/pages/admin/AdminForgotPassword";
import AdminResetPassword from "@/pages/admin/AdminResetPassword";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminDishes from "@/pages/admin/AdminDishes";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminEnquiries from "@/pages/admin/AdminEnquiries";
import AdminQuotes from "@/pages/admin/AdminQuotes";
import AdminQuoteEditor from "@/pages/admin/AdminQuoteEditor";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminBookingDetail from "@/pages/admin/AdminBookingDetail";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminEmailLog from "@/pages/admin/AdminEmailLog";
import "@/App.css";

function ProtectedAdmin({ children }) {
  const { adminToken } = useApp();
  if (!adminToken) return <Navigate to="/admin/login" replace />;
  return children;
}

export default function App() {
  return (
    <AppProvider>
      <div className="App">
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: "hsl(var(--card))",
              color: "hsl(var(--foreground))",
              border: "1px solid hsl(var(--border))",
            },
          }}
        />
        <BrowserRouter>
          <ScrollToTop />
          <WhatsAppConversionTracker />
          <CallConversionTracker />
          <Routes>
            {/* Customer site */}
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/wedding-catering-mumbai" element={<WeddingCateringMumbai />} />
              <Route path="/corporate-catering-mumbai" element={<CorporateCateringMumbai />} />
              <Route path="/birthday-family-catering-mumbai" element={<BirthdayFamilyCateringMumbai />} />
              <Route path="/vegetarian-catering-mumbai" element={<VegetarianCateringMumbai />} />
              <Route path="/catering-for-large-events-mumbai" element={<CateringForLargeEventsMumbai />} />
              <Route path="/menu" element={<Menu />} />
              <Route path="/packages" element={<Packages />} />
              <Route path="/build" element={<BuildYourMenu />} />
              <Route path="/build-your-menu" element={<BuildYourMenu />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/quote" element={<RequestQuote />} />
              <Route path="/request-quote" element={<RequestQuote />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/terms" element={<Terms />} />
            </Route>

            {/* Customer accounts (Module 3 · Phase 1) */}
            <Route path="/account/login" element={<CustomerLogin />} />
            <Route path="/account/forgot-password" element={<CustomerForgotPassword />} />
            <Route path="/account/reset-password" element={<CustomerResetPassword />} />
            <Route path="/account/register" element={<CustomerRegister />} />
            <Route
              path="/account"
              element={
                <ProtectedCustomer>
                  <CustomerDashboard />
                </ProtectedCustomer>
              }
            />
            <Route
              path="/account/profile"
              element={
                <ProtectedCustomer>
                  <ProfileEdit />
                </ProtectedCustomer>
              }
            />
            <Route
              path="/account/quotes/:id"
              element={
                <ProtectedCustomer>
                  <QuoteReview />
                </ProtectedCustomer>
              }
            />
            <Route
              path="/account/bookings/:id"
              element={
                <ProtectedCustomer>
                  <BookingDetail />
                </ProtectedCustomer>
              }
            />
            <Route
              path="/account/quotes/:id/print"
              element={
                <ProtectedCustomer>
                  <PrintableQuote scope="customer" />
                </ProtectedCustomer>
              }
            />
            <Route
              path="/quote/:id/view"
              element={<PrintableQuote scope="public" />}
            />
            <Route
              path="/admin/quotes/:id/print"
              element={
                <ProtectedAdmin>
                  <PrintableQuote scope="admin" />
                </ProtectedAdmin>
              }
            />

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/recovery" element={<AdminRecovery />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route
              path="/admin"
              element={
                <ProtectedAdmin>
                  <AdminLayout />
                </ProtectedAdmin>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="dishes" element={<AdminDishes />} />
              <Route path="packages" element={<AdminPackages />} />
              <Route path="enquiries" element={<AdminEnquiries />} />
              <Route path="quotes" element={<AdminQuotes />} />
              <Route path="quotes/:id" element={<AdminQuoteEditor />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="bookings/:id" element={<AdminBookingDetail />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="emails" element={<AdminEmailLog />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AppProvider>
  );
}
